/**
 * The Memory page (slice 1d spec §6.1): the workspace's shared files and each
 * agent's own, and the chosen file's editor. What each piece shows comes from
 * `memory-view.ts`; the editor's draft meets saves, conflicts and live
 * changes there too.
 *
 * One tree, two layouts, by the width the page has (a container query, not
 * the viewport: the Crew panel can take 400px of a desktop). Narrow, as on a
 * phone, a tablet or beside the open panel, the Workspace card and each
 * agent's card (its files as tabs) stack over the editor. Wide (42rem of
 * content, "wide" below: never below `lg`, where the page's padding and the
 * sidebar leave less), the same cards merge into one sticky file list on the
 * left, with the editor filling the rest of the width.
 *
 * An Areas section follows Workspace (slice 6d spec §8.6): each live Area's
 * brief, which opens its own editor. Anyone in the crew can edit those.
 */
import { AgentAvatar } from "@crew/ui/components/crew-avatar";
import { Card, CardContent, CardHeader, CardTitle } from "@crew/ui/components/card";
import { Link } from "@crew/ui/components/link";
import { Spinner } from "@crew/ui/components/spinner";
import { BackLink } from "@crew/ui/components/back-link";
import { PageTitle } from "@crew/ui/components/typography";
import { cn } from "@crew/ui/lib/utils";
import {
  CalendarDays,
  IdCard,
  NotebookPen,
  Sparkles,
  UserRound,
  Users,
  Volume2,
  type LucideIcon,
} from "lucide-react";
import { useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { agentTint } from "../../app/tints";
import { ChatIcon } from "@crew/ui/components/chat-icon";
import { useAreas, type AreaListItem } from "../areas/api";
import { briefHref } from "../areas/brief";
import { useMemoryFiles, type MemoryList } from "./api";
import { DailyNotes } from "./daily-notes";
import { FileEditor } from "./file-editor";
import {
  agentPhrase,
  isDailyNotes,
  keepsDailyNotes,
  memorySections,
  pickEntry,
  unsavedIds,
  withFile,
  type Drafts,
  type Entry,
  type Sections,
} from "./memory-view";

const ICONS: Record<string, LucideIcon> = {
  "USER.md": UserRound,
  "VOICE.md": Volume2,
  "CREW.md": Users,
  "MEMORY.md": NotebookPen,
  "daily-notes": CalendarDays,
  "SOUL.md": Sparkles,
  "IDENTITY.md": IdCard,
};

/** The wide list's section labels ("Workspace", "Crew"), like the sidebar's group labels. */
const SECTION_LABEL = "text-meta font-semibold tracking-label text-muted-foreground uppercase";

/**
 * A wide row: the app sidebar's 34px row, and its active treatment (a
 * raised row with a hairline). On a white card the raise is the muted wash.
 */
const WIDE_ROW =
  "@2xl/memory:h-[34px] @2xl/memory:gap-2.5 @2xl/memory:rounded-nav @2xl/memory:px-2 @2xl/memory:py-0 @2xl/memory:text-nav @2xl/memory:font-medium @2xl/memory:text-copy @2xl/memory:hover:bg-foreground/[0.04] @2xl/memory:hover:text-foreground";
const WIDE_ACTIVE =
  "@2xl/memory:bg-muted @2xl/memory:text-foreground @2xl/memory:shadow-nav @2xl/memory:hover:bg-muted @2xl/memory:[&_svg]:text-foreground";

export function MemoryPage({ me }: { me: Me }) {
  const files = useMemoryFiles();
  const chief = me.agents.find((a) => a.slug === "main") ?? me.agents[0];
  return (
    <main className="flex w-full max-w-4xl flex-col gap-6 px-4 pt-10 pb-16 sm:px-6 md:px-12 lg:max-w-6xl lg:px-8 xl:px-12 lg:gap-5 lg:pt-8 lg:pb-8">
      <BackLink href="/settings">Settings</BackLink>
      <header className="flex flex-col gap-1.5 lg:gap-1">
        <PageTitle>Memory</PageTitle>
        <p className="text-body text-muted-foreground">
          What your {chief?.name ?? "crew"} knows and remembers across every thread
        </p>
      </header>
      {files.data ? (
        <MemoryFiles list={files.data} me={me} />
      ) : files.error ? (
        <p className="text-body text-muted-foreground">{errorHint(files.error)}</p>
      ) : (
        <Spinner className="size-5 text-muted-foreground" />
      )}
    </main>
  );
}

/**
 * The files by section, and the one the URL names open in the editor. Drafts
 * live here, by file id, so moving to another file and back keeps one; the
 * list marks the files that have one.
 */
function MemoryFiles({ list, me }: { list: MemoryList; me: Me }) {
  const areas = useAreas();
  const [params] = useSearchParams();
  const [drafts, setDrafts] = useState<Drafts>({});
  const sections = memorySections(list.files, me.agents, { dailyNotes: keepsDailyNotes(me) });
  const selected = pickEntry(sections, params);
  const unsaved = unsavedIds(drafts);
  const id = selected?.meta?.id;
  return (
    <div className="@container/memory">
      <div
        data-memory-layout
        className="flex flex-col gap-6 @2xl/memory:grid @2xl/memory:grid-cols-[14.5rem_minmax(0,1fr)] @4xl/memory:grid-cols-[18rem_minmax(0,1fr)] @2xl/memory:items-start @2xl/memory:gap-5 @4xl/memory:gap-6"
      >
        <MemoryFileList
          sections={sections}
          selected={selected}
          unsaved={unsaved}
          areas={areas.data?.areas}
        />
        {isDailyNotes(selected) && selected?.agent ? (
          <DailyNotes key={selected.key} agent={selected.agent} me={me} />
        ) : selected && id ? (
          <FileEditor
            key={id}
            entry={selected}
            me={me}
            budget={list.budget}
            draft={drafts[id]}
            onDraft={(update) => setDrafts((d) => ({ ...d, [id]: update(d[id]) }))}
          />
        ) : (
          <p className="text-body text-muted-foreground">This file isn't set up yet.</p>
        )}
      </div>
    </div>
  );
}

/**
 * Every file, to pick one: the Workspace card and each agent's card, stacked
 * when narrow; when wide, one sticky list card with a "Crew" section.
 */
export function MemoryFileList({
  sections,
  selected,
  unsaved,
  areas = [],
}: {
  sections: Sections;
  selected: Entry | undefined;
  unsaved: Set<string>;
  areas?: AreaListItem[];
}) {
  return (
    <div
      data-memory-list
      className="grid gap-4 @2xl/memory:sticky @2xl/memory:top-6 @2xl/memory:flex @2xl/memory:max-h-[calc(100dvh-3rem)] @2xl/memory:flex-col @2xl/memory:gap-4 @2xl/memory:overflow-y-auto @2xl/memory:rounded-xl @2xl/memory:bg-card @2xl/memory:p-2 @2xl/memory:ring-1 @2xl/memory:ring-foreground/10"
    >
      <WorkspaceCard entries={sections.workspace} selected={selected} unsaved={unsaved} />
      {areas.length > 0 ? <AreasCard areas={areas} /> : null}
      <div className="flex flex-col gap-4 @2xl/memory:gap-3">
        <p className={cn(SECTION_LABEL, "hidden px-2 pt-1 @2xl/memory:block")}>Crew</p>
        {sections.agents.map(({ agent, entries }) => (
          <AgentCard
            key={agent.id}
            agent={agent}
            entries={entries}
            selected={selected}
            unsaved={unsaved}
          />
        ))}
      </div>
    </div>
  );
}

/** Its own card on a phone; inside the list card when wide, with no card of its own. */
const FLAT_WHEN_WIDE =
  "@2xl/memory:gap-1 @2xl/memory:overflow-visible @2xl/memory:rounded-none @2xl/memory:bg-transparent @2xl/memory:py-0 @2xl/memory:ring-0";

/**
 * Context, Voice and Crew: a row each, with its icon and, when narrow, what
 * it's for (the editor's header says so when wide).
 */
function WorkspaceCard({
  entries,
  selected,
  unsaved,
}: {
  entries: Entry[];
  selected: Entry | undefined;
  unsaved: Set<string>;
}) {
  const { search } = useLocation();
  return (
    <Card size="sm" aria-label="Workspace files" className={FLAT_WHEN_WIDE}>
      <CardHeader className="@2xl/memory:px-2 @2xl/memory:pt-1">
        <CardTitle className={SECTION_LABEL}>Workspace</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-0.5 px-1.5 @2xl/memory:px-0">
        {entries.map((entry) => {
          const Icon = ICONS[entry.kind.name] ?? UserRound;
          const active = entry.key === selected?.key;
          return (
            <Link
              key={entry.key}
              href={withFile(search, entry)}
              aria-current={active ? "page" : undefined}
              data-memory-link={entry.key}
              className={cn(
                "flex items-center gap-3 rounded-nav px-2 py-2 outline-none hover:bg-foreground/[0.04] focus-visible:ring-2 focus-visible:ring-ring",
                WIDE_ROW,
                active && cn("bg-muted hover:bg-muted", WIDE_ACTIVE),
              )}
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-well bg-muted text-copy @2xl/memory:size-4 @2xl/memory:bg-transparent @2xl/memory:text-muted-foreground">
                <Icon className="size-4" strokeWidth={1.75} />
              </span>
              <span className="flex min-w-0 flex-col @2xl/memory:flex-1">
                <span className="flex items-center gap-1.5 text-nav font-medium text-foreground @2xl/memory:text-inherit">
                  {entry.kind.title}
                  <UnsavedDot show={unsaved.has(entry.meta?.id ?? "")} />
                </span>
                <span className="truncate text-xs text-muted-foreground @2xl/memory:hidden">
                  {entry.kind.about("your crew")}
                </span>
              </span>
            </Link>
          );
        })}
      </CardContent>
    </Card>
  );
}

/** Each live Area's brief, to its editor (spec §8.6). */
function AreasCard({ areas }: { areas: AreaListItem[] }) {
  return (
    <Card size="sm" aria-label="Areas" className={FLAT_WHEN_WIDE} data-memory-areas>
      <CardHeader className="@2xl/memory:px-2 @2xl/memory:pt-1">
        <CardTitle className={SECTION_LABEL}>Areas</CardTitle>
        <p className="text-xs text-muted-foreground">Anyone in the crew can edit these.</p>
      </CardHeader>
      <CardContent className="flex flex-col gap-0.5 px-1.5 @2xl/memory:px-0">
        {areas.map((area) => (
          <Link
            key={area.id}
            href={briefHref(area.slug)}
            data-memory-area={area.slug}
            className={cn(
              "flex items-center gap-3 rounded-nav px-2 py-2 outline-none hover:bg-foreground/[0.04] focus-visible:ring-2 focus-visible:ring-ring",
              WIDE_ROW,
            )}
          >
            <span className="flex size-8 shrink-0 items-center justify-center rounded-well bg-muted text-copy @2xl/memory:size-4 @2xl/memory:bg-transparent @2xl/memory:text-muted-foreground">
              <ChatIcon name={area.icon} size={16} />
            </span>
            <span className="flex min-w-0 flex-col @2xl/memory:flex-1">
              <span className="truncate text-nav font-medium text-foreground @2xl/memory:text-inherit">
                {area.label}
              </span>
              <span className="truncate text-xs text-muted-foreground @2xl/memory:hidden">
                What the crew knows about it
              </span>
            </span>
          </Link>
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * An agent: its avatar and name, and its Notes, Personality and Identity:
 * as tabs when narrow, and as rows indented under its name when wide.
 */
function AgentCard({
  agent,
  entries,
  selected,
  unsaved,
}: {
  agent: Me["agents"][number];
  entries: Entry[];
  selected: Entry | undefined;
  unsaved: Set<string>;
}) {
  const { search } = useLocation();
  const tint = agentTint(agent.tint);
  return (
    <Card size="sm" aria-label={`${agent.name}'s files`} className={FLAT_WHEN_WIDE}>
      <CardHeader className="flex items-center gap-2.5 @2xl/memory:px-2">
        <AgentAvatar glyph={agent.glyph} tint={tint} size={28} className="@2xl/memory:hidden" />
        <AgentAvatar
          glyph={agent.glyph}
          tint={tint}
          size={20}
          className="hidden @2xl/memory:flex"
        />
        <div className="flex min-w-0 flex-col">
          <CardTitle className="text-nav font-semibold">
            {/* Back to the agent's page (slice 1e spec §4). */}
            <Link
              href={`/crew/${agent.slug}`}
              data-agent-link={agent.slug}
              className="rounded-sm py-2 outline-none hover:underline hover:decoration-foreground/30 hover:underline-offset-[3px] focus-visible:ring-2 focus-visible:ring-ring"
            >
              {agent.name}
            </Link>
          </CardTitle>
          <span className="truncate text-xs text-muted-foreground @2xl/memory:hidden">
            What {agentPhrase(agent)} keeps, and who it is
          </span>
        </div>
      </CardHeader>
      <CardContent className="@2xl/memory:px-0">
        <nav
          aria-label={`${agent.name}'s files`}
          className="flex w-full items-center rounded-lg bg-muted p-[3px] @2xl/memory:flex-col @2xl/memory:items-stretch @2xl/memory:gap-0.5 @2xl/memory:rounded-none @2xl/memory:bg-transparent @2xl/memory:p-0"
        >
          {entries.map((entry) => {
            const Icon = ICONS[entry.kind.name] ?? NotebookPen;
            const active = entry.key === selected?.key;
            return (
              <Link
                key={entry.key}
                href={withFile(search, entry)}
                aria-current={active ? "page" : undefined}
                data-memory-link={entry.key}
                className={cn(
                  "flex h-7 flex-auto items-center justify-center gap-1.5 rounded-md px-2 text-sm whitespace-nowrap font-medium text-foreground/60 outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring",
                  WIDE_ROW,
                  "@2xl/memory:flex-none @2xl/memory:justify-start @2xl/memory:pl-9 @2xl/memory:[&_svg]:text-muted-foreground",
                  active && cn("bg-background text-foreground shadow-sm", WIDE_ACTIVE),
                )}
              >
                <Icon
                  aria-hidden
                  className="hidden size-3.5 shrink-0 @2xl/memory:block"
                  strokeWidth={1.75}
                />
                <span className="@2xl/memory:flex-1">{entry.kind.title}</span>
                <UnsavedDot show={unsaved.has(entry.meta?.id ?? "")} />
              </Link>
            );
          })}
        </nav>
      </CardContent>
    </Card>
  );
}

/** A copper dot on a file with unsaved changes. */
function UnsavedDot({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <span
      data-unsaved
      role="img"
      aria-label="Unsaved changes"
      className="size-1.5 shrink-0 rounded-full bg-copper @2xl/memory:ml-auto"
    />
  );
}
